const NOTIFICATION_SOUND_PATH = 'notification.mp3';

let audioElement: HTMLAudioElement | null = null;
let audioContext: AudioContext | null = null;
let unlockBound = false;

const getAudioContext = (): AudioContext | null => {
  const AudioContextCtor = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextCtor) {
    return null;
  }

  if (!audioContext) {
    audioContext = new AudioContextCtor();
  }

  return audioContext;
};

const resolveNotificationSoundUrl = (): string => {
  if (typeof document === 'undefined') {
    return `/${NOTIFICATION_SOUND_PATH}`;
  }

  const script = document.querySelector<HTMLScriptElement>('script[src*="shahagent-webchat.js"]');
  if (script?.src) {
    try {
      return new URL(NOTIFICATION_SOUND_PATH, script.src).href;
    } catch {
      // Fall through to the app origin.
    }
  }

  return `/${NOTIFICATION_SOUND_PATH}`;
};

const getAudioElement = (): HTMLAudioElement | null => {
  if (typeof Audio === 'undefined') {
    return null;
  }

  if (!audioElement) {
    const audio = new Audio(resolveNotificationSoundUrl());
    audio.preload = 'auto';
    audio.volume = 0.7;
    audioElement = audio;
  }

  return audioElement;
};

const playFallbackChime = async (): Promise<void> => {
  const context = getAudioContext();
  if (!context) {
    return;
  }

  if (context.state === 'suspended') {
    await context.resume();
  }

  const now = context.currentTime;
  const oscillator = context.createOscillator();
  const gain = context.createGain();

  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(880, now);
  oscillator.frequency.setValueAtTime(1175, now + 0.12);

  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.08, now + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);

  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start(now);
  oscillator.stop(now + 0.3);
};

export const unlockWebWidgetNotificationSound = (): void => {
  if (process.env.NODE_ENV === 'test') {
    return;
  }

  const context = getAudioContext();
  if (context?.state === 'suspended') {
    void context.resume();
  }

  const audio = getAudioElement();
  if (!audio) {
    return;
  }

  audio.muted = true;
  try {
    const playResult = audio.play();
    if (playResult && typeof playResult.then === 'function') {
      void playResult.then(() => {
        audio.pause();
        audio.currentTime = 0;
        audio.muted = false;
      }).catch(() => {
        audio.muted = false;
      });
      return;
    }
  } catch {
    // Ignore unlock failures in environments without media playback.
  }

  audio.muted = false;
};

export const bindWebWidgetNotificationSoundUnlock = (): (() => void) => {
  if (typeof window === 'undefined' || unlockBound) {
    return () => undefined;
  }

  unlockBound = true;
  const unlock = () => {
    unlockWebWidgetNotificationSound();
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
  };

  window.addEventListener('pointerdown', unlock);
  window.addEventListener('keydown', unlock);

  return () => {
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
    unlockBound = false;
  };
};

export const playWebWidgetNotificationSound = async (): Promise<void> => {
  const audio = getAudioElement();
  if (audio) {
    try {
      audio.muted = false;
      audio.currentTime = 0;
      const playResult = audio.play();
      if (playResult && typeof playResult.then === 'function') {
        await playResult;
        return;
      }

      if (playResult === undefined) {
        return;
      }
    } catch {
      // Autoplay or missing asset: fall through to the generated chime.
    }
  }

  try {
    await playFallbackChime();
  } catch {
    // Ignore notification sound failures.
  }
};

export const resetWebWidgetNotificationSoundForTests = (): void => {
  audioElement = null;
  if (audioContext) {
    void audioContext.close().catch(() => undefined);
    audioContext = null;
  }
  unlockBound = false;
};
